export interface PatchOperationProps {
  selectedMasterProductId: Product['product_id'];
  activeResourceType: ResourceType | undefined;
  ignore?: boolean;
  selectedInstances: Instance[];
  autoMappingEnabled: boolean;
  productToMap?: MasterProduct | null;
  orgId: string;
}

export interface UpdateResourcesProps {
  ignore?: boolean;
}

export interface CreateAndMapProps {
  fieldData: CreateNewFieldData;
  accessToken: string;
}

export interface SaveResourceMappingProps {
  activeResourceType: ResourceType;
  activeCropwiseResourceId: Product['product_id'];
  orgId: string;
  selectedInstances: Instance[];
}

export interface SuccessfulMappingProps {
  successes: string[];
  referenceId: string;
  activeResource: Product | null;
  ignore?: boolean;
}

export type UnresolvedResourcesOptions = {
  user_id: string;
  org_id: string;
  since?: string;
  before?: string;
  limit?: number;
  needs_resolving?: boolean;
  operation_type?: string;
  next_token?: string;
};

export type GetInstancesByProductParams = {
  org_id: string;
  product_id: string;
  since?: string;
  before?: string;
  limit?: number;
  operation_type?: string;
};

export type PatchResourcesBody = {
  operation_ids: string[];
  mapping: {
    fields?: Record<string, string>;
    products?: Record<string, string>;
  };
  auto_mapping_enabled: boolean;
};

export type ResourceMappingBody = {
  organization_id: string;
  resource_id: string;
  cw_resource_id: string;
  model_scope: ModelScope;
  system_type: string;
  description: string;
  expiration?: Date | string;
};

export type CreateNewFieldData = {
  name: string | undefined;
  parent_region_id: string | undefined;
  property_id: string | undefined;
  declared_area: number | undefined;
  geometry: any;
};

export enum ResourceMapperNotificationTypes {
  PATCH_FAIL,
  PATCH_SUCCESS,
  NO_RESOURCES,
  RESOURCE_ERROR,
  CROPWISE_RESOURCE_ERROR,
  NO_SELECTED_INSTANCES,
  NO_SELECTED_CROPWISE_RESOURCE,
  PATCH_ERROR,
  UNDO_PATCH_ERROR,
  CREATE_NEW_FIELD_ERROR,
  CREATE_NEW_FIELD_IN_PROGRESS,
  NO_SELECTED_INSTANCES_CREATE_NEW_FIELD,
  IGNORE_PRODUCT_FAILURE,
  IGNORE_PRODUCT_SUCCESS,
}

export enum ResourceType {
  product = 'PRODUCT',
  person = 'PERSON',
  equipment = 'EQUIPMENT',
  field = 'FIELD',
}

export enum ModelScope {
  product = 'PRODUCT',
  field = 'FIELD',
  person = 'PARTY',
  equipment = 'ASSET',
}

export enum OperationType {
  ALL = 'all',
  APPLICATION = 'application',
  HARVEST = 'harvest',
  PLANTING = 'planting',
  TILLAGE = 'tillage',
}

export type Product = {
  product_id: string;
  description: string;
  is_resolved?: boolean;
  aliases: Array<{
    id: string;
    instance_system_id: string;
    model_scope: string;
    system_type: string;
  }>;
  form: string;
  product_type: string;
  product_components: Array<{
    product_ref: string;
    is_carrier: boolean;
    quantity: {
      value: string;
    };
  }>;
  measures?: Array<{
    uom: string;
    dtr_code: string;
    value: string;
    user_uom: string;
  }>;
  mix_measures?: Array<{
    uom: string;
    dtr_code: string;
    value: string;
    user_uom: string;
  }>;
  total_instance_count: number;
  instances: Pick<Instance, 'id'>[];
};

export type Instance = {
  id: string;
  instance_id: string;
  aliases: Aliase[];
  product_id: string;
  description: string;
  parent_operation: Operation;
  center: number[] | undefined;
  references: any;
};

export type Aliase = {
  id: string;
  model_scope: string;
  system_type: string;
  instance_system_id: string;
};

export type Operation = {
  id: string;
  start_date_time: string;
  end_date_time: string;
  operation_type: string;
  machineData?: any[];
  task_measures: any;
  organization_ref: string; // org id
  organization_value: string; // org name
  fields?: {
    id: string;
    description: string;
  }[];
  farms?: {
    id: string;
    description: string;
  }[];
  parties?: Array<{
    name: string;
  }>;
};

export type MachineData = {
  id: string;
  parties?: {
    id: string;
    description: string;
    party_type: string;
    aliases: Aliase[];
  }[];
  assets?: {
    description: string;
    aliases: Aliase[];
  }[];
  product_uses?: {
    resource_ref: string;
    resource_measures: {
      dtr_code: string;
      value: string;
      uom: string;
      user_uom: string;
    }[];
  }[];
};

export interface MatchingHistoryItem {
  mapping_id: string;
  source_product_name: string;
  cropwise_product_name: string;
  matched_status: string;
  integration_name: string;
  product_type: string;
  last_modified_date: string;
  operation_list: Array<string>;
}

export type MasterProduct = {
  product_id: string;
  product_name: string;
  unit_of_measure?: string;
  manufacturer_name?: string;
  category?: string;
  created: string;
  modified: string;
};

export type FiltersState = {
  isTankMixShown: boolean;
  dateRange: DateRange;
  operationType: OperationType;
};

export type DateRange = {
  start: moment.Moment | undefined;
  end: moment.Moment | undefined;
};

export type FilterProps<T> = {
  update: (value: T) => void;
  value: T;
};

export type ActiveIntegrationOption = {
  integrationName: string;
  integrationId: string;
};
